// usePointerMode などからの「ユーザー操作」を CanvasOp へ変換し、
// 楽観更新 (applyOptimistic) → realtime クライアント送信を一括で行う dispatcher。

import { useCallback, useRef } from "react";
import { useShowToast } from "@meiden-connect/ui";
import {
  CANVAS_OBJECT_HARD_LIMIT,
  CANVAS_OBJECT_SOFT_LIMIT,
  clampCanvasObjectToCanvas,
  clampPointToCanvas,
  computePenStrokeFrame,
  isCanvasObjectInsideCanvas,
  type CanvasOp,
  type CanvasObject,
  type PenStrokeCanvasObject,
  type StickyCanvasObject,
  type TextCanvasObject,
  type ShapeCanvasObject,
  type ImageCanvasObject,
  type VideoCanvasObject,
  type LinkCardCanvasObject,
} from "@refinement-canvas/domain";
import { customAlphabet } from "nanoid";
import { useCanvasDocStore } from "./store/canvas-doc-store";
import { useRealtimeClient } from "../../realtime/realtime-context";

const generateId = customAlphabet("23456789abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ", 16);

interface DispatcherContext {
  readonly actorUserId: string;
}

// CanvasObject は discriminated union のため、Omit を distributive に展開して
// 個別 type の draft 入力（text / shapeKind / src 等）を保持できるようにする。
type DistributiveDraft<T> = T extends CanvasObject
  ? Omit<T, "id" | "version" | "createdAt" | "createdBy" | "updatedAt" | "updatedBy" | "zOrder"> & {
      readonly id?: string | undefined;
    }
  : never;

export type CanvasObjectDraft = DistributiveDraft<CanvasObject>;

export interface CanvasOpDispatcher {
  readonly createObject: (object: CanvasObjectDraft) => CanvasObject | null;
  readonly createObjects: (objects: readonly CanvasObjectDraft[]) => readonly CanvasObject[];
  readonly transformObject: (objectId: string, transform: ObjectTransformPatch) => void;
  /**
   * 複数オブジェクトの transform を **1 回の履歴エントリ** にまとめて適用する。
   * グループドラッグなど「ユーザーから見れば 1 操作」を 1 回の Undo で戻せるようにする。
   */
  readonly transformObjects: (
    updates: readonly { readonly objectId: string; readonly transform: ObjectTransformPatch }[]
  ) => void;
  readonly updateText: (objectId: string, text: string) => void;
  readonly updateAttachmentSrc: (objectId: string, src: string) => void;
  readonly updateCanvasMeta: (input: { title?: string | undefined; thumbnailKey?: string | undefined }) => void;
  readonly deleteObject: (objectId: string) => void;
  readonly deleteObjects: (objectIds: readonly string[]) => void;
  readonly undo: () => void;
  readonly redo: () => void;
  readonly commitPenStroke: (
    points: ReadonlyArray<{ x: number; y: number; pressure: number }>,
    color: string,
    thickness: number,
    boundingBox: { x: number; y: number; w: number; h: number }
  ) => PenStrokeCanvasObject | null;
}

export type ObjectTransformPatch = Partial<{
  x: number;
  y: number;
  w: number;
  h: number;
  rotation: number;
  zOrder: number;
  locked: boolean;
}>;

interface HistoryEntry {
  readonly undo: readonly CanvasOp[];
  readonly redo: readonly CanvasOp[];
}

const undoStack: HistoryEntry[] = [];
const redoStack: HistoryEntry[] = [];
const HISTORY_LIMIT = 100;

export function clearCanvasHistory(): void {
  undoStack.length = 0;
  redoStack.length = 0;
}

export function useCanvasOpDispatcher(ctx: DispatcherContext): CanvasOpDispatcher {
  const applyOptimistic = useCanvasDocStore((s) => s.applyOptimistic);
  const realtime = useRealtimeClient();
  const showToast = useShowToast();
  const lastSoftLimitToastAtRef = useRef(0);

  const sendOp = useCallback(
    (op: CanvasOp): void => {
      applyOptimistic(op);
      realtime.sendOp(op);
    },
    [applyOptimistic, realtime]
  );

  const dispatch = useCallback(
    (op: CanvasOp, history?: HistoryEntry | undefined): void => {
      if (history) {
        undoStack.push(history);
        if (undoStack.length > HISTORY_LIMIT) undoStack.shift();
        redoStack.length = 0;
      }
      sendOp(op);
    },
    [sendOp]
  );

  const dispatchMany = useCallback(
    (ops: readonly CanvasOp[], history?: HistoryEntry | undefined): void => {
      if (ops.length === 0) return;
      if (history) {
        undoStack.push(history);
        if (undoStack.length > HISTORY_LIMIT) undoStack.shift();
        redoStack.length = 0;
      }
      for (const op of ops) {
        sendOp(op);
      }
    },
    [sendOp]
  );

  const makeMeta = useCallback(
    () => ({
      clientOpId: generateId(),
      clientCreatedAt: new Date().toISOString(),
      actorUserId: ctx.actorUserId,
    }),
    [ctx.actorUserId]
  );

  const canCreateObjects = useCallback(
    (addCount: number): boolean => {
      const currentCount = Object.keys(useCanvasDocStore.getState().objects).length;
      const nextCount = currentCount + addCount;
      if (nextCount > CANVAS_OBJECT_HARD_LIMIT) {
        showToast(
          "error",
          `このキャンバスはオブジェクト数の上限（${CANVAS_OBJECT_HARD_LIMIT.toLocaleString()}件）に達しています。新しいキャンバスへ分割して作業を続けてください。`
        );
        return false;
      }

      if (nextCount >= CANVAS_OBJECT_SOFT_LIMIT) {
        const now = Date.now();
        if (now - lastSoftLimitToastAtRef.current > 30_000) {
          lastSoftLimitToastAtRef.current = now;
          showToast(
            "warning",
            `このキャンバスのオブジェクト数が多くなっています（${nextCount.toLocaleString()}件）。動作が重い場合は、新しいキャンバスへの分割をご検討ください。`
          );
        }
      }
      return true;
    },
    [showToast]
  );

  const notifyOutOfBounds = useCallback((): void => {
    showToast("warning", "キャンバス範囲外には新しいオブジェクトを作成できません。");
  }, [showToast]);

  const createObject = useCallback<CanvasOpDispatcher["createObject"]>(
    (input) => {
      if (!canCreateObjects(1)) return null;
      const id = input.id ?? generateId();
      const now = new Date().toISOString();
      const objects = useCanvasDocStore.getState().objects;
      const nextZ = computeNextZOrder(objects);
      const object = clampCanvasObjectToCanvas({
        ...input,
        id,
        version: 1,
        zOrder: nextZ,
        createdAt: now,
        createdBy: ctx.actorUserId,
        updatedAt: now,
        updatedBy: ctx.actorUserId,
      } as CanvasObject);
      if (!isCanvasObjectInsideCanvas(object)) {
        notifyOutOfBounds();
        return null;
      }
      const op: CanvasOp = {
        type: "object.create",
        clientOpId: generateId(),
        clientCreatedAt: now,
        actorUserId: ctx.actorUserId,
        object,
      };
      dispatch(op, {
        undo: [makeDeleteOp(object.id, ctx.actorUserId)],
        redo: [op],
      });
      return object;
    },
    [canCreateObjects, ctx.actorUserId, dispatch, notifyOutOfBounds]
  );

  const createObjects = useCallback<CanvasOpDispatcher["createObjects"]>(
    (inputs) => {
      if (inputs.length === 0) return [];
      if (!canCreateObjects(inputs.length)) return [];
      const now = new Date().toISOString();
      const objects = useCanvasDocStore.getState().objects;
      let nextZ = computeNextZOrder(objects);
      const created: CanvasObject[] = [];
      for (const input of inputs) {
        const object = clampCanvasObjectToCanvas({
          ...input,
          id: input.id ?? generateId(),
          version: 1,
          zOrder: nextZ,
          createdAt: now,
          createdBy: ctx.actorUserId,
          updatedAt: now,
          updatedBy: ctx.actorUserId,
        } as CanvasObject);
        if (!isCanvasObjectInsideCanvas(object)) {
          notifyOutOfBounds();
          return [];
        }
        created.push(object);
        nextZ += 1;
      }
      const ops: CanvasOp[] = created.map((object) => ({
        type: "object.create",
        clientOpId: generateId(),
        clientCreatedAt: now,
        actorUserId: ctx.actorUserId,
        object,
      }));
      dispatchMany(ops, {
        undo: created.map((object) => makeDeleteOp(object.id, ctx.actorUserId)),
        redo: ops,
      });
      return created;
    },
    [canCreateObjects, ctx.actorUserId, dispatchMany, notifyOutOfBounds]
  );

  const transformObject = useCallback<CanvasOpDispatcher["transformObject"]>(
    (objectId, transform) => {
      const current = useCanvasDocStore.getState().objects[objectId];
      if (!current) return;
      // ロック中はロック切替（locked のみ）以外の transform を最終防衛として一切送らない。
      // ドラッグ・リサイズ・回転は pointerdown 時の locked チェックを行うが、開始後 pointerup
      // までの間に他クライアントから locked: true が反映されたケースを救う必要がある。
      if (current.locked && !isLockOnlyTransform(transform)) return;
      const inverse = pickCurrentTransform(current, transform);
      const op: CanvasOp = {
        type: "object.transform",
        ...makeMeta(),
        objectId,
        transform,
      };
      dispatch(op, {
        undo: [{ type: "object.transform", ...makeMeta(), objectId, transform: inverse }],
        redo: [op],
      });
    },
    [dispatch, makeMeta]
  );

  // 複数オブジェクトの transform を 1 回の履歴エントリにまとめる。
  // グループドラッグなど「1 ユーザー操作 = 複数 op」を 1 回の Ctrl+Z で戻せるようにする。
  const transformObjects = useCallback<CanvasOpDispatcher["transformObjects"]>(
    (updates) => {
      if (updates.length === 0) return;
      const objects = useCanvasDocStore.getState().objects;
      const ops: CanvasOp[] = [];
      const undo: CanvasOp[] = [];

      for (const { objectId, transform } of updates) {
        const current = objects[objectId];
        if (!current) continue;
        // 個別 transformObject と同じ locked ガード。グループ内に locked が混ざっていても
        // 個別にスキップして残りは通す（=1 entry にロック解除前の状態を保持できる）。
        if (current.locked && !isLockOnlyTransform(transform)) continue;
        const inverse = pickCurrentTransform(current, transform);
        const op: CanvasOp = { type: "object.transform", ...makeMeta(), objectId, transform };
        ops.push(op);
        undo.push({ type: "object.transform", ...makeMeta(), objectId, transform: inverse });
      }

      if (ops.length === 0) return;
      dispatchMany(ops, { undo, redo: ops });
    },
    [dispatchMany, makeMeta]
  );

  const updateText = useCallback<CanvasOpDispatcher["updateText"]>(
    (objectId, text) => {
      const current = useCanvasDocStore.getState().objects[objectId];
      // ロック中は本文更新も行わない。useObjectTextEditor 側でも編集モードを抜けて
      // 保留中の debounce timer は破棄しているが、UI イベントや blur のタイミング差で
      // この経路に到達しても契約を守れるよう dispatcher 側でも防御する。
      if (!current || !("text" in current) || current.locked || current.text === text) return;
      const op: CanvasOp = {
        type: "object.text.update",
        ...makeMeta(),
        objectId,
        text,
      };
      dispatch(op, {
        undo: [{ type: "object.text.update", ...makeMeta(), objectId, text: current.text }],
        redo: [op],
      });
    },
    [dispatch, makeMeta]
  );

  const updateAttachmentSrc = useCallback<CanvasOpDispatcher["updateAttachmentSrc"]>(
    (objectId, src) => {
      const current = useCanvasDocStore.getState().objects[objectId];
      if (!current) return;
      const previousSrc = getAttachmentSrc(current);
      const op: CanvasOp = {
        type: "object.attachment.update",
        ...makeMeta(),
        objectId,
        src,
      };
      dispatch(op, {
        undo: previousSrc
          ? [{ type: "object.attachment.update", ...makeMeta(), objectId, src: previousSrc }]
          : [],
        redo: [op],
      });
    },
    [dispatch, makeMeta]
  );

  const updateCanvasMeta = useCallback<CanvasOpDispatcher["updateCanvasMeta"]>(
    (input) => {
      const current = useCanvasDocStore.getState().meta;
      const nextTitle = input.title?.trim();
      const op: CanvasOp = {
        type: "canvas.meta.update",
        ...makeMeta(),
        ...(nextTitle ? { title: nextTitle } : {}),
        ...(input.thumbnailKey !== undefined ? { thumbnailKey: input.thumbnailKey } : {}),
      };
      if (op.title === undefined && op.thumbnailKey === undefined) return;
      dispatch(op, {
        undo: [
          {
            type: "canvas.meta.update",
            ...makeMeta(),
            ...(op.title !== undefined ? { title: current.title } : {}),
            ...(op.thumbnailKey !== undefined && current.thumbnailKey !== undefined
              ? { thumbnailKey: current.thumbnailKey }
              : {}),
          },
        ],
        redo: [op],
      });
    },
    [dispatch, makeMeta]
  );

  const deleteObject = useCallback<CanvasOpDispatcher["deleteObject"]>(
    (objectId) => {
      const current = useCanvasDocStore.getState().objects[objectId];
      if (!current) return;
      const op = makeDeleteOp(objectId, ctx.actorUserId);
      dispatch(op, {
        undo: [makeCreateOp(current, ctx.actorUserId)],
        redo: [op],
      });
    },
    [ctx.actorUserId, dispatch]
  );

  const deleteObjects = useCallback<CanvasOpDispatcher["deleteObjects"]>(
    (objectIds) => {
      if (objectIds.length === 0) return;
      const objects = useCanvasDocStore.getState().objects;
      const targets = objectIds.map((id) => objects[id]).filter((o): o is CanvasObject => Boolean(o));
      if (targets.length === 0) return;
      const ops = targets.map((object) => makeDeleteOp(object.id, ctx.actorUserId));
      dispatchMany(ops, {
        undo: targets.map((object) => makeCreateOp(object, ctx.actorUserId)),
        redo: ops,
      });
    },
    [ctx.actorUserId, dispatchMany]
  );

  const commitPenStroke = useCallback<CanvasOpDispatcher["commitPenStroke"]>(
    (points, color, thickness, _boundingBox) => {
      if (!canCreateObjects(1)) return null;
      const id = generateId();
      const now = new Date().toISOString();
      const objects = useCanvasDocStore.getState().objects;
      const nextZ = computeNextZOrder(objects);
      const clippedPoints = points.map((point) => clampPointToCanvas(point));
      const boundingBox = computePenStrokeFrame(clippedPoints, thickness);
      const stroke: PenStrokeCanvasObject = {
        type: "pen-stroke",
        id,
        x: boundingBox.x,
        y: boundingBox.y,
        w: boundingBox.w,
        h: boundingBox.h,
        zOrder: nextZ,
        version: 1,
        color,
        thickness,
        points: clippedPoints,
        createdAt: now,
        createdBy: ctx.actorUserId,
        updatedAt: now,
        updatedBy: ctx.actorUserId,
      };
      const op: CanvasOp = {
        type: "pen.stroke.commit",
        clientOpId: generateId(),
        clientCreatedAt: now,
        actorUserId: ctx.actorUserId,
        object: stroke,
      };
      dispatch(op, {
        undo: [makeDeleteOp(stroke.id, ctx.actorUserId)],
        redo: [op],
      });
      return stroke;
    },
    [canCreateObjects, ctx.actorUserId, dispatch]
  );

  const undo = useCallback((): void => {
    const entry = undoStack.pop();
    if (!entry) return;
    // ロック衝突がある場合は履歴に戻して何もしない（再ロック解除後は再度 Ctrl+Z 可能）。
    // dispatcher.transformObject の locked ガードを履歴経路でも迂回されないようにする。
    if (hasLockedTransformConflict(entry.undo)) {
      undoStack.push(entry);
      return;
    }
    for (const op of entry.undo) {
      sendOp(refreshOpMeta(op, ctx.actorUserId));
    }
    redoStack.push(entry);
  }, [ctx.actorUserId, sendOp]);

  const redo = useCallback((): void => {
    const entry = redoStack.pop();
    if (!entry) return;
    if (hasLockedTransformConflict(entry.redo)) {
      redoStack.push(entry);
      return;
    }
    for (const op of entry.redo) {
      sendOp(refreshOpMeta(op, ctx.actorUserId));
    }
    undoStack.push(entry);
  }, [ctx.actorUserId, sendOp]);

  return {
    createObject,
    createObjects,
    transformObject,
    transformObjects,
    updateText,
    updateAttachmentSrc,
    updateCanvasMeta,
    deleteObject,
    deleteObjects,
    undo,
    redo,
    commitPenStroke,
  };
}

function makeCreateOp(object: CanvasObject, actorUserId: string): CanvasOp {
  return {
    type: "object.create",
    clientOpId: generateId(),
    clientCreatedAt: new Date().toISOString(),
    actorUserId,
    object,
  };
}

function makeDeleteOp(objectId: string, actorUserId: string): CanvasOp {
  return {
    type: "object.delete",
    clientOpId: generateId(),
    clientCreatedAt: new Date().toISOString(),
    actorUserId,
    objectId,
  };
}

function refreshOpMeta(op: CanvasOp, actorUserId: string): CanvasOp {
  return {
    ...op,
    clientOpId: generateId(),
    clientCreatedAt: new Date().toISOString(),
    actorUserId,
  } as CanvasOp;
}

// transform が「locked のみで構成される＝ロック切替」かを判定する。
// ロック中でもロック切替（特に解除）の経路は通したいので、ガード判定の鍵となる。
function isLockOnlyTransform(transform: ObjectTransformPatch): boolean {
  const transformKeys = Object.keys(transform);
  return transformKeys.length > 0 && transformKeys.every((key) => key === "locked");
}

// undo / redo に積まれた op 集合のうち、ロック中オブジェクトに対する
// ロック切替以外の object.transform が含まれるかを判定する。
// 含まれている場合、そのエントリは履歴に戻して送信スキップする。
function hasLockedTransformConflict(ops: readonly CanvasOp[]): boolean {
  return ops.some((op) => {
    if (op.type !== "object.transform") return false;
    const current = useCanvasDocStore.getState().objects[op.objectId];
    return current?.locked === true && !isLockOnlyTransform(op.transform);
  });
}

function pickCurrentTransform(
  object: CanvasObject,
  requested: ObjectTransformPatch
): ObjectTransformPatch {
  const inverse: ObjectTransformPatch = {};
  if (requested.x !== undefined) inverse.x = object.x;
  if (requested.y !== undefined) inverse.y = object.y;
  if (requested.w !== undefined) inverse.w = object.w;
  if (requested.h !== undefined) inverse.h = object.h;
  if (requested.rotation !== undefined) inverse.rotation = object.rotation ?? 0;
  if (requested.zOrder !== undefined) inverse.zOrder = object.zOrder;
  if (requested.locked !== undefined) inverse.locked = object.locked ?? false;
  return inverse;
}

function getAttachmentSrc(object: CanvasObject): string | undefined {
  if (object.type === "image" || object.type === "video") return object.src;
  if (object.type === "link-card") return object.imageUrl;
  return undefined;
}

/** 既存オブジェクトの最大 zOrder + 1 を返す（簡易な「最前面」割り当て）。 */
function computeNextZOrder(objects: Record<string, CanvasObject>): number {
  let max = 0;
  for (const obj of Object.values(objects)) {
    if (obj.zOrder > max) max = obj.zOrder;
  }
  return max + 1;
}

// 補助型: createObject に渡す引数を 1 種類ずつ作るためのヘルパー。
export type StickyDraftInput = Omit<
  StickyCanvasObject,
  "id" | "version" | "createdAt" | "createdBy" | "updatedAt" | "updatedBy" | "zOrder"
>;
export type TextDraftInput = Omit<
  TextCanvasObject,
  "id" | "version" | "createdAt" | "createdBy" | "updatedAt" | "updatedBy" | "zOrder"
>;
export type ShapeDraftInput = Omit<
  ShapeCanvasObject,
  "id" | "version" | "createdAt" | "createdBy" | "updatedAt" | "updatedBy" | "zOrder"
>;
export type ImageDraftInput = Omit<
  ImageCanvasObject,
  "id" | "version" | "createdAt" | "createdBy" | "updatedAt" | "updatedBy" | "zOrder"
>;
export type VideoDraftInput = Omit<
  VideoCanvasObject,
  "id" | "version" | "createdAt" | "createdBy" | "updatedAt" | "updatedBy" | "zOrder"
>;
export type LinkCardDraftInput = Omit<
  LinkCardCanvasObject,
  "id" | "version" | "createdAt" | "createdBy" | "updatedAt" | "updatedBy" | "zOrder"
>;
